<script setup lang="ts">
import { DonutChart } from '@/components/charts/DonutChart'
import { StackedColumnChart } from '@/components/charts/StackedColumnChart'

import type { WalletUsageStatistics } from '@/types/wallet'

const props = defineProps<{
  statistics: WalletUsageStatistics
}>()

const { t } = useI18n()
const { statistics } = toRefs(props)
const walletStatisticsTranslationKey = 'pages.loyaltySupports.wallet.statistics.'
const currentModal = ref(null)
const chartWalletStatus = ref(new DonutChart())
const chartStorePenetration = ref(new StackedColumnChart())
const totalCustomers = ref(0)

const isStoreChartVisible = computed(() => {
  return statistics.value.stores.length > 2
})

const statisticCards = [
  {
    id: 'active',
    title: t(`${walletStatisticsTranslationKey}totalActives.card.title`),
    value: statistics.value.passes.active,
    clickable: true,
    ctaTitle: t(`${walletStatisticsTranslationKey}totalActives.card.cta`),
    modalTitle: t(`${walletStatisticsTranslationKey}totalActives.modal.title`),
    modalContent: t(`${walletStatisticsTranslationKey}totalActives.modal.description`),
    isOpen: false
  },
  {
    id: 'deleted',
    title: t(`${walletStatisticsTranslationKey}totalDeletions.card.title`),
    value: statistics.value.passes.deleted,
    type: 'supprimés',
    clickable: true,
    ctaTitle: t(`${walletStatisticsTranslationKey}totalDeletions.card.cta`),
    modalTitle: t(`${walletStatisticsTranslationKey}totalDeletions.modal.title`),
    isOpen: false
  }
]

chartStorePenetration.value.series = [
  {
    name: t('pages.loyaltySupports.wallet.statistics.active'),
    data: statistics.value.stores.map((store) => store.passes.active)
  },
  {
    name: t('pages.loyaltySupports.wallet.statistics.deleted'),
    data: statistics.value.stores.map((store) => store.passes.deleted)
  }
]

const statisticsTableColumns = {
  pagination: {
    total: null,
    lastPage: null,
    pageSize: 5,
    currentPage: 1,
    page: 1
  },
  columns: {
    name: {
      label: t(`${walletStatisticsTranslationKey}table.columns.store`)
    },
    count: {
      label: t(`${walletStatisticsTranslationKey}table.columns.count`),
      numeric: true
    },
    customerPercentage: {
      label: t(`${walletStatisticsTranslationKey}table.columns.customerPercentage`),
      numeric: true
    }
  },
  sort: {
    sortBy: 'customerPercentage',
    sortOrder: 'desc'
  }
}

const tableRows = computed(() => {
  return statistics.value.stores.map((store) => {
    return {
      data: {
        name: store.store.name,
        count: store.passes[currentModal.value.id],
        customerPercentage: `${
          currentModal.value.id === 'active'
            ? store.percentageOfTotalCustomerMerchantsInThisStoreWithActivePass
            : store.percentageOfTotalCustomerMerchantsInThisStoreWithDeletedPass
        } %`
      }
    }
  })
})

const series = [statistics.value.passes.active, statistics.value.passes.deleted]

const walletStatusLabels = [
  t('pages.loyaltySupports.wallet.statistics.active'),
  t('pages.loyaltySupports.wallet.statistics.deleted')
]

const getTotalCustomers = (value: number, percentage: number) => {
  return Math.floor((value * 100) / percentage) || 0
}

const poulateDonutChart = () => {
  chartWalletStatus.value.series = series
  chartWalletStatus.value.setLabels(walletStatusLabels)
  chartStorePenetration.value.setColor(['#F8D169', '#74CA78', '#EC654E'])
  chartStorePenetration.value.setCategories(statistics.value.stores.map((store) => store.store.name))
}

const openModal = (selectedCard: any) => {
  selectedCard.isOpen = true
  currentModal.value = selectedCard
}

onMounted(() => {
  poulateDonutChart()
  totalCustomers.value = getTotalCustomers(
    statistics.value.passes.active,
    statistics.value.percentageOfTotalCustomerMerchants.active
  )
  statistics.value.stores?.map((store) => [store.passes.downloaded, store.passes.active, store.passes.deleted])
})
</script>

<template>
  <dl class="mt-5 grid gap-5 xl:grid-cols-2">
    <AppStatsCard
      v-for="card in statisticCards"
      :key="card.id"
      class="text-left"
      :clickable="true"
      :title="card.title"
      @click="openModal(card)"
    >
      <div class="flex w-full items-end justify-between">
        <p class="text-4xl font-extrabold">
          <span>{{ card.value }}</span>
        </p>
        <p class="text-xs font-medium underline">{{ card.ctaTitle }}</p>
      </div>
    </AppStatsCard>
  </dl>
  <div>
    <div class="mt-5 grid w-full grid-cols-2 gap-5 md:grid-cols-2">
      <div
        class="w-full items-center justify-center border bg-white pt-4 text-center sm:rounded-lg"
        :class="{ 'col-span-2': !isStoreChartVisible }"
      >
        <span class="mt-4 text-center text-sm font-bold">{{
          t('pages.loyaltySupports.wallet.statistics.charts.burgerChartTitle')
        }}</span>
        <apexchart
          :type="chartWalletStatus.type"
          :options="chartWalletStatus.options"
          :series="chartWalletStatus.series"
          class="mt-10 p-4"
          :class="{ 'mx-auto max-w-(--breakpoint-sm)': !isStoreChartVisible }"
        />
      </div>
      <div v-if="isStoreChartVisible" class="w-full items-center border bg-white pt-4 text-center sm:rounded-lg">
        <span class="text-center text-sm font-bold">{{
          t('pages.loyaltySupports.wallet.statistics.charts.storeChartTitle')
        }}</span>
        <apexchart
          :type="chartStorePenetration.type"
          :options="chartStorePenetration.options"
          :series="chartStorePenetration.series"
          class="flex self-end p-10 align-bottom"
        />
      </div>
    </div>
  </div>
  <BaseModal v-if="currentModal" :is-open="true" :heading-title="currentModal.modalTitle" @close="currentModal = null">
    <AppTable :columns="statisticsTableColumns.columns" :rows="tableRows" />
    <div class="mt-4 text-left text-sm italic">
      {{ currentModal.modalContent }}
    </div>
  </BaseModal>
</template>
